(() => {
  if (window.PeakStyleProductUI) return;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const mobileView = window.matchMedia('(max-width: 767.98px), (hover: none), (pointer: coarse)');
  let activeOverlay = null;
  let activeTrigger = null;
  let pinnedOverlay = null;
  let closeTimer = null;

  const overlayFor = (trigger) => {
    const id = trigger?.dataset.target || trigger?.getAttribute('aria-controls');
    if (!id) return null;
    const localRoot = trigger.closest('product-info, .f-modal--quickview') || document;
    return localRoot.querySelector(`#${CSS.escape(id)}`) || document.getElementById(id);
  };

  const rememberOrigin = (overlay) => {
    if (overlay.__peakstyleOrigin) return;
    overlay.__peakstyleOrigin = { parent: overlay.parentNode, next: overlay.nextSibling };
  };

  const restoreOrigin = (overlay) => {
    const origin = overlay?.__peakstyleOrigin;
    if (!origin?.parent?.isConnected) return;
    if (origin.next && origin.next.parentNode === origin.parent) origin.parent.insertBefore(overlay, origin.next);
    else origin.parent.appendChild(overlay);
  };

  const controlsFor = (overlay) => {
    if (!overlay?.id) return [];
    return document.querySelectorAll(`[data-peakstyle-size-chart-toggle][aria-controls="${CSS.escape(overlay.id)}"]`);
  };

  const isPinned = (overlay) => Boolean(
    overlay && pinnedOverlay === overlay && overlay.dataset.peakstylePinned === 'true'
  );

  const setPinned = (overlay, pinned) => {
    if (!overlay) return;

    if (pinned) {
      if (pinnedOverlay && pinnedOverlay !== overlay) {
        pinnedOverlay.dataset.peakstylePinned = 'false';
        pinnedOverlay.classList.remove('is-pinned');
        controlsFor(pinnedOverlay).forEach((button) => button.setAttribute('aria-pressed', 'false'));
      }
      pinnedOverlay = overlay;
      overlay.dataset.peakstylePinned = 'true';
      overlay.classList.add('is-pinned');
    } else {
      overlay.dataset.peakstylePinned = 'false';
      overlay.classList.remove('is-pinned');
      if (pinnedOverlay === overlay) pinnedOverlay = null;
    }

    controlsFor(overlay).forEach((button) => button.setAttribute('aria-pressed', pinned ? 'true' : 'false'));
  };

  const close = (overlay = activeOverlay, restoreFocus = false) => {
    if (!overlay) return;
    window.clearTimeout(closeTimer);
    setPinned(overlay, false);
    overlay.hidden = true;
    overlay.classList.remove('peakstyle-size-chart-overlay--global', 'peakstyle-size-chart-overlay--media');
    document.documentElement.classList.remove('peakstyle-size-chart-open');
    document.body.classList.remove('peakstyle-size-chart-open');
    controlsFor(overlay).forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-pressed', 'false');
    });
    restoreOrigin(overlay);
    if (restoreFocus && activeTrigger?.isConnected) activeTrigger.focus();
    if (activeOverlay === overlay) activeOverlay = null;
  };

  const open = (trigger, focusClose = false) => {
    const overlay = overlayFor(trigger);
    if (!overlay) return;
    if (activeOverlay && activeOverlay !== overlay) close(activeOverlay);
    rememberOrigin(overlay);
    const quickView = trigger.closest('.f-modal--quickview');
    const useGlobal = Boolean(quickView) || mobileView.matches;

    if (useGlobal) {
      document.body.appendChild(overlay);
      overlay.classList.add('peakstyle-size-chart-overlay--global');
      document.documentElement.classList.add('peakstyle-size-chart-open');
      document.body.classList.add('peakstyle-size-chart-open');
      if (quickView && !quickView.__peakstyleSizeChartCleanup) {
        quickView.__peakstyleSizeChartCleanup = true;
        quickView.addEventListener('close', () => {
          if (activeOverlay === overlay) close(overlay);
          if (overlay.isConnected && !quickView.contains(overlay)) overlay.remove();
        });
      }
    } else {
      const productInfo = trigger.closest('product-info');
      const sectionId = productInfo?.dataset.section;
      const gallery = sectionId ? document.getElementById(`GalleryViewer-${sectionId}`) || productInfo.querySelector(`#MediaGallery-${CSS.escape(sectionId)}`) : null;
      if (gallery) {
        if (getComputedStyle(gallery).position === 'static') gallery.style.position = 'relative';
        gallery.appendChild(overlay);
      }
      overlay.classList.add('peakstyle-size-chart-overlay--media');
    }

    overlay.hidden = false;
    controlsFor(overlay).forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
      if (!button.hasAttribute('aria-pressed')) button.setAttribute('aria-pressed', 'false');
    });
    activeOverlay = overlay;
    activeTrigger = trigger;
    if (focusClose) overlay.querySelector('[data-peakstyle-size-chart-close]')?.focus();
  };

  const scheduleClose = (overlay) => {
    if (!overlay || isPinned(overlay)) return;
    window.clearTimeout(closeTimer);
    closeTimer = window.setTimeout(() => {
      if (isPinned(overlay)) return;
      if (!activeTrigger?.matches(':hover') && !overlay?.matches(':hover')) close(overlay);
    }, 160);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-peakstyle-size-chart-toggle]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      const overlay = overlayFor(trigger);
      if (!overlay) return;

      const desktopPinnable = finePointer.matches && !trigger.closest('.f-modal--quickview');
      if (desktopPinnable) {
        if (isPinned(overlay)) {
          close(overlay, false);
        } else {
          if (overlay.hidden) open(trigger, false);
          else {
            activeOverlay = overlay;
            activeTrigger = trigger;
          }
          window.clearTimeout(closeTimer);
          setPinned(overlay, true);
        }
      } else if (!overlay.hidden) {
        close(overlay, mobileView.matches);
      } else {
        open(trigger, true);
      }
      return;
    }

    const closer = event.target.closest?.('[data-peakstyle-size-chart-close]');
    if (closer) {
      event.preventDefault();
      event.stopPropagation();
      close(closer.closest('[data-peakstyle-size-chart-overlay]'), true);
    }
  }, true);

  document.addEventListener('mouseover', (event) => {
    if (!finePointer.matches) return;
    const trigger = event.target.closest?.('[data-peakstyle-size-chart-toggle]');
    if (trigger && !trigger.closest('.f-modal--quickview') && !trigger.contains(event.relatedTarget)) {
      event.stopPropagation();
      window.clearTimeout(closeTimer);
      open(trigger, false);
      return;
    }
    const overlay = event.target.closest?.('[data-peakstyle-size-chart-overlay]');
    if (overlay) window.clearTimeout(closeTimer);
  }, true);

  document.addEventListener('mouseout', (event) => {
    if (!finePointer.matches) return;
    const trigger = event.target.closest?.('[data-peakstyle-size-chart-toggle]');
    if (trigger && !trigger.closest('.f-modal--quickview') && !trigger.contains(event.relatedTarget)) {
      event.stopPropagation();
      const overlay = overlayFor(trigger);
      if (isPinned(overlay)) return;
      scheduleClose(overlay);
      return;
    }
    const overlay = event.target.closest?.('[data-peakstyle-size-chart-overlay]');
    if (overlay && !overlay.contains(event.relatedTarget)) {
      if (isPinned(overlay)) {
        event.stopPropagation();
        return;
      }
      scheduleClose(overlay);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeOverlay) {
      event.preventDefault();
      close(activeOverlay, true);
    }
  });

  window.PeakStyleProductUI = {
    open,
    close,
    pin: (trigger) => {
      const overlay = overlayFor(trigger);
      if (!overlay) return;
      if (overlay.hidden) open(trigger, false);
      setPinned(overlay, true);
    },
    unpin: (overlay = pinnedOverlay) => setPinned(overlay, false),
    isPinned
  };
})();
